/**
 * Branding — Step 22's first slice: the app's own name (Kai: customisable,
 * never fixed). Global default + per-org override; an empty override inherits.
 * Logo, colours and the rest of white-label arrive with the full Step 22.
 */
import { useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon, Upload } from 'lucide-react';
import { brandColourError } from '@itsm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiSend } from '../../lib/api';
import { useDirty } from '../../lib/dirty';
import { qk } from '../../lib/queryClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { useBranding } from '../../lib/useBranding';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';

interface BrandingSettings {
  appName: { effective: string; org: string | null; global?: string | null };
}

export function BrandingPage() {
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.brandingSettings,
    queryFn: () => apiFetch<BrandingSettings>('/api/admin/branding'),
  });

  // One organisation per deployment (2026-09-14): the org row is the only
  // row this screen edits. The global row remains as the seeded default.
  const scope = 'org' as const;

  const stored = useMemo(() => data?.appName.org ?? '', [data]);
  const [draft, setDraft] = useState('');
  useEffect(() => setDraft(stored ?? ''), [stored]);
  const dirty = draft !== (stored ?? '');
  useDirty(dirty);

  const save = useMutation({
    mutationFn: () => apiSend('/api/admin/branding', 'PUT', { scope, key: 'appName', value: draft || null }),
    onSuccess: (res) => {
      if (!res.ok) return;
      void queryClient.invalidateQueries({ queryKey: qk.brandingSettings });
      void queryClient.invalidateQueries({ queryKey: qk.branding }); // shell + titles update immediately
    },
  });

  if (isPending) {
    return (
      <div className="max-w-xl space-y-3 p-6">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="m-6 max-w-xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load branding</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl p-6">
      <h1 className="mb-1 text-lg font-semibold">Branding</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Currently showing everywhere as{' '}
        <span className="font-medium text-foreground">{data!.appName.effective}</span>.
      </p>

      <div className="space-y-3 rounded-lg border bg-card p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">App name</label>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`leave empty to use “${data!.appName.global ?? data!.appName.effective}”`}
            maxLength={80}
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            The product's name, shown everywhere. Empty = the default.
          </p>
        </div>
        {save.data && !save.data.ok && <p className="text-xs text-destructive">{save.data.error}</p>}
        <div className="flex items-center justify-end gap-3 border-t pt-3">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button variant="ghost" disabled={!dirty || save.isPending} onClick={() => setDraft(stored ?? '')}>
            Discard
          </Button>
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <AppearanceEditor />
      <VocabularyEditor />
    </div>
  );
}

/**
 * The white-label vocabulary — Step 79.
 *
 * Draft-first: this is a table of free text, which is exactly what the
 * standard says must never auto-save. One Save writes the whole patch.
 *
 * Every box shows the DEFAULT as its placeholder rather than its value, so an
 * empty box reads as "inherits 'ticket'" instead of a missing setting — and
 * clearing one is how you go back, with no separate reset control to explain.
 */
function VocabularyEditor() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, { one?: string; many?: string }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loaded = useQuery({
    queryKey: qk.vocabulary,
    queryFn: () =>
      apiFetch<{
        overrides: Record<string, { one?: string; many?: string }>;
        defaults: Record<string, { one: string; many: string }>;
      }>('/api/admin/branding/vocabulary'),
  });

  const stored = loaded.data?.overrides ?? {};
  const current = draft ?? stored;
  const dirty = JSON.stringify(current) !== JSON.stringify(stored);
  useDirty(dirty);

  if (loaded.isPending || loaded.isError) return null;
  const defaults = loaded.data.defaults;

  const set = (key: string, part: 'one' | 'many', value: string) => {
    const next = { ...current, [key]: { ...current[key], [part]: value } };
    // An emptied box means "inherit", so the key goes rather than storing "".
    if (!next[key]!.one?.trim() && !next[key]!.many?.trim()) delete next[key];
    setDraft(next);
  };

  return (
    <div className="mt-8 rounded-lg border bg-card p-4">
      <h2 className="text-sm font-semibold">What things are called</h2>
      <p className="mb-3 mt-0.5 text-sm text-muted-foreground">
        Rename the words this organisation sees. Leave a box empty to keep the default.
      </p>
      {error && <p className="mb-2 text-xs text-destructive">{error}</p>}
      <div className="space-y-1.5">
        <div className="grid grid-cols-[1fr,1fr] gap-2 px-1 text-[11px] font-medium text-muted-foreground">
          <span>One</span>
          <span>Many</span>
        </div>
        {Object.entries(defaults).map(([key, def]) => (
          <div key={key} className="grid grid-cols-[1fr,1fr] gap-2">
            <Input
              className="h-9 text-sm"
              placeholder={def.one}
              value={current[key]?.one ?? ''}
              onChange={(e) => set(key, 'one', e.target.value)}
            />
            <Input
              className="h-9 text-sm"
              placeholder={def.many}
              value={current[key]?.many ?? ''}
              onChange={(e) => set(key, 'many', e.target.value)}
            />
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end gap-3 border-t pt-3">
        {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        <Button variant="ghost" disabled={!dirty || saving} onClick={() => setDraft(null)}>
          Discard
        </Button>
        <Button
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true);
            setError(null);
            const res = await apiSend('/api/admin/branding/vocabulary', 'PUT', { overrides: current });
            setSaving(false);
            if (!res.ok) return setError(res.error ?? 'That could not be saved.');
            setDraft(null);
            void qc.invalidateQueries({ queryKey: qk.vocabulary });
            // The nouns render from the cached branding query, so it has to be
            // refetched or the rename does not appear until a reload.
            void qc.invalidateQueries({ queryKey: qk.branding });
          }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Step 135 — logo, favicon, colour, display name, corners.
 *
 * `organisation_branding` had existed since Step 12 and NOTHING wrote to it;
 * the shell painted faithfully from a row no admin could set. Kai: *"upload
 * images, favicons etc."* This is the screen.
 *
 * Images go through `/api/media` (validated, cleaned, size-capped) and are
 * then CLAIMED by the appearance PUT, which re-checks the id is this
 * organisation's. Draft-first for the text and colour; the two uploads commit
 * on choose, because a picked file IS the decision and a Save button between
 * choosing a logo and seeing it would only add a step.
 */
/** Exported for the setup wizard (Step 165): the wizard embeds THIS editor
 *  rather than a second copy of the upload and colour rules. */
export function AppearanceEditor() {
  const qc = useQueryClient();
  const branding = useBranding();
  const [draft, setDraft] = useState<{ displayName: string; brandColor: string; radius: string } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'logo' | 'logoDark' | 'favicon' | 'save' | null>(null);
  const stored = {
    displayName: branding?.name ?? '',
    brandColor: (branding as unknown as { brandColor?: string | null })?.brandColor ?? '#2563eb',
    radius: (branding as unknown as { radius?: string | null })?.radius ?? '0.5rem',
  };
  const current = draft ?? stored;
  const dirty = JSON.stringify(current) !== JSON.stringify(stored);
  useDirty(dirty);
  const colourError = brandColourError(current.brandColor);
  const logoPath = (branding as unknown as { logoPath?: string | null })?.logoPath ?? null;
  const logoDarkPath = (branding as unknown as { logoDarkPath?: string | null })?.logoDarkPath ?? null;
  const faviconPath = (branding as unknown as { faviconPath?: string | null })?.faviconPath ?? null;
  const version = (branding as unknown as { version?: number })?.version ?? 0;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.branding });
  };

  // The dark logo shares the logo's constraints (PNG/JPEG/WebP, 2 MB); only
  // the favicon differs. One map keeps upload, save and remove in step.
  const ATTACH_FIELD = {
    logo: 'logoAttachmentId',
    logoDark: 'logoDarkAttachmentId',
    favicon: 'faviconAttachmentId',
  } as const;
  const PURPOSE = { logo: 'brand_logo', logoDark: 'brand_logo', favicon: 'brand_favicon' } as const;
  type ImgKind = keyof typeof ATTACH_FIELD;

  /** Upload one image and point the branding at it — one gesture. */
  async function pick(kind: ImgKind, file: File) {
    setBusy(kind);
    setError(null);
    const fd = new FormData();
    fd.append('purpose', PURPOSE[kind]);
    fd.append('file', file);
    const up = await apiSend<{ id: string }>('/api/media', 'POST', fd);
    if (!up.ok || !up.body?.id) {
      setBusy(null);
      return setError(up.error ?? 'That image could not be uploaded.');
    }
    const res = await apiSend('/api/admin/branding/appearance', 'PUT', {
      [ATTACH_FIELD[kind]]: up.body.id,
    });
    setBusy(null);
    if (!res.ok) return setError(res.error ?? 'That could not be saved.');
    refresh();
  }

  return (
    <div className="mt-8 rounded-lg border bg-card p-4">
      <h2 className="text-sm font-semibold">Appearance</h2>
      <p className="mb-3 mt-0.5 text-sm text-muted-foreground">
        The name, logo, colour and tab icon everyone sees — agents and requesters alike.
      </p>
      {error && <p className="mb-2 text-xs text-destructive">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Display name</label>
          <Input
            value={current.displayName}
            placeholder="Acme Support"
            onChange={(e) => setDraft({ ...current, displayName: e.target.value })}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Brand colour</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Brand colour"
              value={/^#[0-9a-f]{6}$/i.test(current.brandColor) ? current.brandColor : '#2563eb'}
              onChange={(e) => setDraft({ ...current, brandColor: e.target.value })}
              className="h-9 w-12 cursor-pointer rounded-md border bg-transparent p-0.5"
            />
            <Input
              value={current.brandColor}
              onChange={(e) => setDraft({ ...current, brandColor: e.target.value })}
              className="font-mono"
            />
          </div>
          {/* The plan's rule, said before Save rather than after: a colour that
              cannot be read against both themes is refused. */}
          <p className={cn('mt-1 text-xs', colourError ? 'text-destructive' : 'text-muted-foreground')}>
            {colourError ?? 'Readable on both light and dark backgrounds.'}
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Corners</label>
          <Select value={current.radius} onChange={(e) => setDraft({ ...current, radius: e.target.value })}>
            <option value="0rem">Square</option>
            <option value="0.25rem">Slight</option>
            <option value="0.5rem">Rounded</option>
            <option value="0.75rem">Soft</option>
            <option value="1rem">Pill-ish</option>
          </Select>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {(
          [
            {
              kind: 'logo',
              label: 'Logo (light)',
              hint: 'Shown on light backgrounds, at 32px tall — a WIDE, landscape logo (roughly 4:1) works best. PNG, JPEG or WebP, up to 2 MB.',
              path: logoPath,
              size: 'h-10',
            },
            {
              kind: 'logoDark',
              label: 'Logo (dark)',
              hint: 'Shown on the dark theme, at 32px tall — a WIDE, landscape logo (roughly 4:1). Falls back to the light logo if unset.',
              path: logoDarkPath,
              size: 'h-10',
            },
            {
              kind: 'favicon',
              label: 'Tab icon',
              hint: 'The square (1:1) mark — the browser tab, the home-screen icon, and the app on a phone. PNG, up to 1 MB.',
              path: faviconPath,
              size: 'h-6 w-6',
            },
          ] as const
        ).map((img) => (
          <div key={img.kind} className="rounded-md border p-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">{img.label}</p>
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'flex h-12 w-24 items-center justify-center rounded',
                  // A dark logo is drawn for a dark header — preview it on one,
                  // whatever theme the admin is in.
                  img.kind === 'logoDark' ? 'bg-slate-900' : 'bg-muted/40',
                )}
              >
                {img.path ? (
                  <img
                    src={`${img.path}?v=${version}`}
                    alt=""
                    className={cn('max-w-full object-contain', img.size)}
                  />
                ) : (
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept={img.kind === 'favicon' ? 'image/png' : 'image/png,image/jpeg,image/webp'}
                  className="sr-only"
                  disabled={busy !== null}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void pick(img.kind, f);
                    e.currentTarget.value = '';
                  }}
                />
                <span className="inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-accent">
                  <Upload className="h-3.5 w-3.5" />{' '}
                  {busy === img.kind ? 'Uploading…' : img.path ? 'Replace' : 'Upload'}
                </span>
              </label>
              {img.path && (
                <button
                  className="text-xs text-muted-foreground hover:underline"
                  disabled={busy !== null}
                  onClick={async () => {
                    const res = await apiSend('/api/admin/branding/appearance', 'PUT', {
                      [ATTACH_FIELD[img.kind]]: null,
                    });
                    if (!res.ok) return setError(res.error ?? 'That could not be removed.');
                    refresh();
                  }}
                >
                  Remove
                </button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">{img.hint}</p>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-end gap-3 border-t pt-3">
        {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        <Button variant="ghost" disabled={!dirty || busy !== null} onClick={() => setDraft(null)}>
          Discard
        </Button>
        <Button
          disabled={!dirty || busy !== null || !!colourError}
          onClick={async () => {
            setBusy('save');
            setError(null);
            const res = await apiSend('/api/admin/branding/appearance', 'PUT', {
              displayName: current.displayName.trim() || null,
              brandColor: current.brandColor.trim(),
              radius: current.radius,
            });
            setBusy(null);
            if (!res.ok) return setError(res.error ?? 'That could not be saved.');
            setDraft(null);
            refresh();
          }}
        >
          {busy === 'save' ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
